import { AuthResponse, LoginCredentials, MfaLoginResponse, RegisterData } from '../types/auth';
import { ErrorHandler } from '../utils/errorHandler';

// Normalize API base URL - remove trailing slashes and ensure /api path
const getApiBaseUrl = (): string => {
  const envUrl = process.env.REACT_APP_API_URL;

  // 1. Explicit env var always wins (set it in .env.development.local for local dev,
  //    e.g. REACT_APP_API_URL=http://localhost:5000)
  if (envUrl) {
    const normalized = envUrl.replace(/\/+$/, '');
    return normalized.endsWith('/api') ? normalized : `${normalized}/api`;
  }

  // 2. In development, use a relative URL so Create React App's `proxy`
  //    (package.json -> "proxy": "http://localhost:5000") forwards /api/*
  //    to the backend. This avoids CORS / wrong-port issues entirely and
  //    works on any hostname (localhost, dashboard.localhost, ...).
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    return '/api';
  }

  // 3. Production fallback: same origin + /api (frontend and backend served together)
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000';
  const normalized = origin.replace(/\/+$/, '');
  return normalized.endsWith('/api') ? normalized : `${normalized}/api`;
};

const API_BASE_URL = getApiBaseUrl();

class AuthAPI {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount: number = 0
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    // Authentication is carried by an httpOnly cookie (see index.tsx fetch
    // wrapper, which adds credentials: 'include' for /api requests).
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      // fetch() only rejects on network-level failures (backend down, CORS
      // blocked, DNS, offline...); those propagate to the outer handler.
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        // 429 fallback: if the body carries no retry timing (proxy or legacy
        // limiter), use the standard Retry-After response header (seconds).
        if (response.status === 429 && errorData && typeof errorData === 'object') {
          const hasTiming =
            errorData.retryAfterSeconds != null ||
            errorData.retryAfterMinutes != null ||
            errorData.retryAfter != null;
          if (!hasTiming) {
            const header = response.headers.get('Retry-After');
            const seconds = header != null ? parseInt(header, 10) : NaN;
            if (!isNaN(seconds) && seconds > 0) {
              errorData.retryAfterSeconds = seconds;
            }
          }
        }

        // 429 helper: the UI can disable submission until the lockout resets.
        // Convert backend seconds to an absolute expiry timestamp (ms) so a
        // page refresh or component remount still knows when to re-enable.
        if (response.status === 429 && errorData && typeof errorData === 'object') {
          const retrySeconds = ErrorHandler.getRetryAfterSeconds(
            ErrorHandler.parseApiError({
              response: { status: 429, data: errorData }
            })
          );
          if (retrySeconds != null && retrySeconds > 0) {
            errorData.retryAfterUntil = Date.now() + retrySeconds * 1000;
          }
        }
        const authError = ErrorHandler.parseApiError({
          response: {
            status: response.status,
            data: errorData
          }
        });

        // If token is expired and we haven't tried refreshing yet, try to refresh.
        // Never attempt a refresh for the unauthenticated auth endpoints themselves
        // (login/register/forgot/reset): a 401 there means bad credentials, and the
        // refresh call would fail too, doubling requests and delaying the real error.
        const isAuthEndpoint = endpoint.startsWith('/auth/login')
          || endpoint.startsWith('/auth/register')
          || endpoint.startsWith('/auth/forgot-password')
          || endpoint.startsWith('/auth/reset-password')
          || endpoint.startsWith('/auth/verify-email')
          || endpoint.startsWith('/auth/resend-verification')
          || endpoint.startsWith('/auth/refresh');
        if (response.status === 401 && retryCount === 0 && !isAuthEndpoint) {
          try {
            await this.refreshToken();
            // Retry the original request with the refreshed cookie
            return this.request<T>(endpoint, options, retryCount + 1);
          } catch (refreshError) {
            // If refresh fails, throw the original error
            throw authError;
          }
        }

        // Retry logic for retryable errors
        if (retryCount < 3 && ErrorHandler.isRetryable(authError)) {
          const delay = ErrorHandler.getRetryDelay(retryCount);
          await new Promise(resolve => setTimeout(resolve, delay));
          return this.request<T>(endpoint, options, retryCount + 1);
        }

        throw authError;
      }

      // Guard against non-JSON responses (e.g. proxy misconfiguration returning
      // index.html). response.json() would throw a SyntaxError ("Unexpected token <").
      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw ErrorHandler.parseApiError({
          response: { status: response.status, data: { message: 'Réponse serveur inattendue (non-JSON). Vérifiez la configuration du proxy API.' } }
        });
      }

      return response.json();
    } catch (error) {
      // Handle network errors and other exceptions
      if (error instanceof Error && !('code' in error)) {
        const authError = ErrorHandler.parseApiError(error);
        throw authError;
      }
      throw error;
    }
  }

  async login(credentials: LoginCredentials & { rememberMe?: boolean }): Promise<MfaLoginResponse> {
    return this.request<MfaLoginResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  }

  // =====================================================
  // TOTP MFA (SEC task 5)
  // =====================================================

  // Enrollment URI + secret for the authenticator app. Called with the
  // challenge returned by /auth/login when mfaSetupRequired, or while
  // already logged in (cookie path).
  async enrollMfa(challenge: string): Promise<{ otpauthUri: string; secret: string }> {
    return this.request<{ otpauthUri: string; secret: string }>(
      `/auth/mfa/enroll?challenge=${encodeURIComponent(challenge)}`,
      { method: 'GET' }
    );
  }

  // First code validation: enables MFA and establishes the session.
  async activateMfa(challenge: string, code: string): Promise<MfaLoginResponse> {
    return this.request<MfaLoginResponse>('/auth/mfa/activate', {
      method: 'POST',
      body: JSON.stringify({ challenge, code }),
    });
  }

  // Daily login for enrolled accounts: { challenge, code } -> session.
  async verifyMfa(challenge: string, code: string): Promise<MfaLoginResponse> {
    return this.request<MfaLoginResponse>('/auth/mfa/verify', {
      method: 'POST',
      body: JSON.stringify({ challenge, code }),
    });
  }

  // Disable MFA (password-confirmed). Privileged accounts are rejected
  // server-side (MFA is mandatory for admins).
  async disableMfa(currentPassword: string): Promise<void> {
    return this.request<void>('/auth/mfa/disable', {
      method: 'POST',
      body: JSON.stringify({ currentPassword }),
    });
  }

  async register(data: RegisterData): Promise<RegisterResponse> {
    return this.request<RegisterResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async logout(): Promise<void> {
    return this.request<void>('/auth/logout', {
      method: 'POST',
    });
  }

  async getProfile(): Promise<AuthResponse['user']> {
    const response = await this.request<{ user: AuthResponse['user'] }>('/auth/profile');
    // Backend returns { user: {...} }, extract the user object
    return response.user;
  }

  async updateProfile(data: Partial<RegisterData>): Promise<AuthResponse['user']> {
    return this.request<AuthResponse['user']>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async forgotPassword(email: string): Promise<void> {
    return this.request<void>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    return this.request<void>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, newPassword }),
    });
  }

  async verifyResetToken(token: string): Promise<{ valid: boolean }> {
    return this.request<{ valid: boolean }>('/auth/verify-reset-token', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async verifyEmail(token: string): Promise<void> {
    return this.request<void>('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async resendVerification(email: string): Promise<void> {
    return this.request<void>('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async refreshToken(): Promise<{ message: string }> {
    return this.request<{ message: string }>('/auth/refresh', {
      method: 'POST',
    });
  }
}

export const authAPI = new AuthAPI();
export type { LoginCredentials, RegisterData, AuthResponse };

// Auth is cookie-based: the backend never returns tokens in bodies, so no
// token/sessionToken fields belong here.
export interface RegisterResponse {
  message: string;
  requiresVerification?: boolean;
  user?: AuthResponse['user'];
}
